"use client";
import React, { createContext, useContext, useState, useCallback } from "react";
import { BASE_URL } from "../config/config";

const CourseContentContext = createContext();

export const CourseContentProvider = ({ children }) => {
  const [courseContent, setCourseContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  // 🔹 FETCH COURSE CONTENT
  const fetchCourseContent = useCallback(async (courseId) => {
    if (!token || !courseId) return;
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BASE_URL}/course-details/${courseId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        const transformedContent = transformApiToComponent(data.data);
        setCourseContent(transformedContent);
        return transformedContent;
      } else {
        setError(data.message || "Failed to fetch course content");
        return null;
      }
    } catch (err) {
      setError("Network error");
      console.error("Fetch course content error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔹 CREATE COURSE CONTENT
  const createCourseContent = async (courseId, contentData) => {
    if (!token || !courseId) return false;
    setLoading(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const formData = new FormData();

      formData.append("course_id", courseId);
      formData.append("create_audit_id", user.id);

      const { modules, videoFiles } = transformComponentToApi(contentData);
      formData.append("modules", JSON.stringify(modules));

      videoFiles.forEach((file, index) => {
        if (file) formData.append(`video_${index}`, file);
      });

      const res = await fetch(`${BASE_URL}/course-details`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (res.ok && (data.status === 200 || data.status === 201)) {
        await fetchCourseContent(courseId);
        return true;
      } else {
        setError(data.message || "Failed to create course content");
        return false;
      }
    } catch (err) {
      setError("Network error");
      console.error("Create course content error:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 🔹 UPDATE COURSE CONTENT
  const updateCourseContent = async (courseId, contentData) => {
    if (!token || !courseId) return false;
    setLoading(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const formData = new FormData();

      formData.append("update_audit_id", user.id);

      const { modules, videoFiles } = transformComponentToApi(contentData);
      formData.append("modules", JSON.stringify(modules));

      videoFiles.forEach((file, index) => {
        if (file instanceof File) formData.append(`video_${index}`, file);
      });

      const res = await fetch(`${BASE_URL}/course-details/${courseId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        await fetchCourseContent(courseId);
        return true;
      } else {
        setError(data.message || "Failed to update course content");
        return false;
      }
    } catch (err) {
      setError("Network error");
      console.error("Update course content error:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 🔹 DELETE COURSE CONTENT
  const deleteCourseContent = async (courseId) => {
    if (!token || !courseId) return false;
    if (!window.confirm("Delete all course content? This action cannot be undone.")) return false;

    setLoading(true);
    setError("");

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const res = await fetch(`${BASE_URL}/course-details/${courseId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id: user.id }),
      });

      const data = await res.json();

      if (res.ok && data.status === 200) {
        setCourseContent(null);
        return true;
      } else {
        setError(data.message || "Failed to delete course content");
        return false;
      }
    } catch (err) {
      setError("Network error");
      console.error("Delete course content error:", err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 🔹 TRANSFORM API RESPONSE TO COMPONENT FORMAT
  const transformApiToComponent = (apiData) => {
    if (!apiData || !apiData.modules) {
      return {
        modules: [],
        finalTest: { title: "", questions: [] },
      };
    }

    let videoIndex = 0;

    const modules = apiData.modules.map((module, moduleIndex) => {
      const concepts = [];
      const mcqQuestions = [];

      module.contents?.forEach((content) => {
        if (content.type === "video") {
          concepts.push({
            id: Date.now() + Math.random(),
            title: content.title || "",
            type: "video",
            file: content.video || null, // ✅ fixed from video_url
            duration: content.duration || "0:00",
            video_index: videoIndex,
          });
          videoIndex++;
        } else if (content.type === "test") {
          content.questions?.forEach((q, qIndex) => {
            mcqQuestions.push({
              id: Date.now() + Math.random() + qIndex,
              question: q.question || "",
              options: Array.isArray(q.options) ? q.options : ["", "", "", ""],
              correctAnswer: q.options?.indexOf(q.answer) || 0,
            });
          });
        }
      });

      return {
        id: Date.now() + moduleIndex,
        title: module.module_name || "",
        concepts: concepts.length
          ? concepts
          : [
              { id: Date.now() + Math.random(), title: "", type: "video", file: null },
            ],
        mcqTest: {
          title: `${module.module_name} Test` || "",
          questions: mcqQuestions.length
            ? mcqQuestions
            : [{ id: Date.now(), question: "", options: ["", "", "", ""], correctAnswer: 0 }],
        },
      };
    });

    return {
      modules: modules.length
        ? modules
        : [
            {
              id: Date.now(),
              title: "",
              concepts: [{ id: Date.now() + 1, title: "", type: "video", file: null }],
              mcqTest: { title: "", questions: [{ id: Date.now() + 2, question: "", options: ["", "", "", ""], correctAnswer: 0 }] },
            },
          ],
      finalTest: {
        title: "Final Course Test",
        questions: [{ id: Date.now() + 3, question: "", options: ["", "", "", ""], correctAnswer: 0 }],
      },
    };
  };

  // 🔹 TRANSFORM COMPONENT DATA TO API FORMAT
  const transformComponentToApi = (componentData) => {
    const videoFiles = [];
    let videoIndex = 0;

    const modules = componentData.modules.map((module) => {
      const contents = [];

      module.concepts?.forEach((concept) => {
        if (concept.type === "video") {
          contents.push({
            type: "video",
            title: concept.title || "",
            duration: concept.duration || "0:00",
            video_index: videoIndex,
          });

          if (concept.file instanceof File) videoFiles[videoIndex] = concept.file;
          videoIndex++;
        }
      });

      if (module.mcqTest?.questions?.length > 0 && module.mcqTest.questions[0].question) {
        const questions = module.mcqTest.questions.map((q) => ({
          question: q.question || "",
          options: q.options || ["", "", "", ""],
          answer: q.options[q.correctAnswer] || q.options[0] || "",
        }));

        contents.push({
          type: "test",
          title: module.mcqTest.title || `${module.title} Test`,
          questions: questions,
        });
      }

      return { module_name: module.title || "", contents };
    });

    return { modules, videoFiles };
  };

  // 🔹 CLEAR CONTENT
  const clearContent = () => {
    setCourseContent(null);
    setError("");
  };

  return (
    <CourseContentContext.Provider
      value={{
        courseContent,
        loading,
        error,
        fetchCourseContent,
        createCourseContent,
        updateCourseContent,
        deleteCourseContent,
        clearContent,
        setCourseContent,
      }}
    >
      {children}
    </CourseContentContext.Provider>
  );
};

export const useCourseContent = () => {
  const context = useContext(CourseContentContext);
  if (!context) throw new Error("useCourseContent must be used within a CourseContentProvider");
  return context;
};
